/**
 * Resolve disabled/readOnly state based on field config, current mode (add vs edit),
 * and current form values (for conditional shapes).
 *
 * Accepted shapes:
 * - true → always
 * - 'add' → in add mode only
 * - 'edit' → in edit mode only
 * - { when?, whenSet? } → conditional, AND across keys; see ConditionalInteraction
 * - false / undefined → not disabled/readOnly
 */
import type { ConditionalInteraction, ConfigField } from '@scio3/shared-types';
import { getNested } from '../utils/nested-keys';

export interface FieldInteraction {
  disabled: boolean;
  readOnly: boolean;
}

type InteractionValue = boolean | 'add' | 'edit' | ConditionalInteraction | undefined;

/** A form value counts as "set" when it is non-null/non-undefined and, for strings or
 *  arrays, non-empty. Numbers `0` and booleans `false` are treated as set.
 *  Exported so `displayOptions` (whenSet inside showIf/hideIf) uses the same predicate. */
export function isSet(val: unknown): boolean {
  if (val == null) return false;
  if (typeof val === 'string') return val.length > 0;
  if (Array.isArray(val)) return val.length > 0;
  return true;
}

function evaluateConditional(
  cond: ConditionalInteraction,
  watchedValues: Record<string, unknown>,
): boolean {
  const whenOk = cond.when
    ? Object.entries(cond.when).every(([key, values]) =>
        values.includes(getNested(watchedValues, key)),
      )
    : true;
  const setOk = cond.whenSet ? cond.whenSet.every((k) => isSet(getNested(watchedValues, k))) : true;
  return whenOk && setOk;
}

export function resolveMode(
  value: InteractionValue,
  isEditMode: boolean,
  watchedValues: Record<string, unknown>,
): boolean {
  if (value === true) return true;
  if (value === 'edit') return isEditMode;
  if (value === 'add') return !isEditMode;
  if (value && typeof value === 'object') return evaluateConditional(value, watchedValues);
  return false;
}

export function resolveInteraction(
  field: ConfigField,
  isEditMode: boolean,
  watchedValues: Record<string, unknown>,
): FieldInteraction {
  return {
    disabled: resolveMode(field.disabled, isEditMode, watchedValues),
    readOnly: resolveMode(field.readOnly, isEditMode, watchedValues),
  };
}
